import { KgType } from '@/lib/knowledge-graph/types'
import { Vertical } from '@/types/verticals'
import type { Waterbody } from '@onxmaps/supergraph'

import fishRouteNames from '@/fish/router/constants'

interface Args {
  id: string
  typename: KgType
  routeState?: Record<string, unknown>
  about?: Record<string, unknown>
}

/**
 * Extract the desired Waterbody parent from a Region for Search results
 */
export const getRegionParent = (
  waterbodies: Waterbody[]
): Waterbody | undefined => {
  if (waterbodies?.length === 1) {
    return waterbodies[0]
  }
  // If Regions have > 1 parent, approximate by guessing parent with largest area
  const sorted = [...(waterbodies ?? [])].sort(
    (a, b) => (b.area || 0) - (a.area || 0)
  )
  return sorted[0]
}

/**
 * Returns a router destination object for navigating to the appropriate card for a given knowledge graph entity type.
 */
export const getRouterDestinationForKgType = ({
  id,
  typename,
  routeState,
  about,
}: Args) => {
  // FishingRegion needs some custom handling
  if (typename === KgType.FISHING_REGION) {
    const parent = getRegionParent(about?.waterbodies as Waterbody[])
    if (parent?.id) {
      return {
        name: fishRouteNames.REGION_WATERBODY_OVERVIEW,
        params: { regionId: id, waterbodyId: parent.id },
        state: routeState,
      }
    }
    return {
      name: fishRouteNames.REGION_OVERVIEW,
      params: { regionId: id },
      state: routeState,
    }
  }

  const routeByType: { [Vertical.Fish]: { [key in KgType]?: object } } = {
    [Vertical.Fish]: {
      [KgType.LAKE]: {
        name: fishRouteNames.WATERBODY,
        params: { id },
        state: routeState,
      },
      [KgType.FISHING_AREA]: {
        name: fishRouteNames.FISHING_AREA,
        params: { areaId: id },
        state: routeState,
      },
      [KgType.WATER_ACCESS]: {
        name: fishRouteNames.WATER_ACCESS_SITE,
        params: { id },
        state: routeState,
      },
    },
  }

  const route = routeByType['fish'][typename]

  if (!route) {
    return undefined
  }

  return route
}

/**
 * Checks if the passed in type is supported by this application
 * @param typename - KgType to check
 */
export const isSupportedKgType = (typename: KgType) => {
  return getRouterDestinationForKgType({ id: '', typename }) !== undefined
}
